export interface Property {
  id: number;
  name: string;
  description: string;
  dateCreated: string;
  dateUpdated: string;
  whoUpdated: string;
}

export interface PropertyWithDetails extends Property {
  /** Primary address association — denormalized for list display */
  addressId: number;
  categoryId: number;
  address: PropertyAddressDetails;
  propertiesAddresses: PropertyAddressAssociation[];
  propertiesFees: PropertyFeeDetails[];
  propertiesAmenities: PropertyAmenityDetails[];
  contacts: ContactDetails[];
  categoryName?: string;
}

export interface PropertyAddressAssociation {
  id: number;
  propertyId: number;
  addressId: number;
  categoryId: number;
  isPrimary: boolean;
  categoryName?: string;
  address: PropertyAddressDetails;
}

export interface PropertyAddressDetails {
  id: number;
  street1: string;
  street2?: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface PropertyAddressFormRow {
  id: number;
  addressId: number;
  categoryId: number;
  isPrimary: boolean;
  street1?: string;
  street2?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export type PropertyFeeInterval = 'daily' | 'weekly' | 'monthly' | 'yearly';

export const PROPERTY_FEE_INTERVALS: Array<{ value: PropertyFeeInterval; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' }
];

export interface PropertyFeeDetails {
  id: number;
  propertyId: number;
  typeId: number;
  description: string;
  amount: number;
  contactId: number;
  interval: PropertyFeeInterval;
  /** mm/dd/yyyy */
  feeDate: string;
  typeName?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactBusinessName?: string;
}

export interface PropertyFeeFormRow {
  id: number;
  typeId: number;
  description: string;
  amount: number;
  contactId: number;
  interval: PropertyFeeInterval;
  /** mm/dd/yyyy */
  feeDate: string;
  typeName?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactBusinessName?: string;
}

export const PROPERTY_AMENITY_LIMITS = {
  brandMaxLength: 50,
  modelMaxLength: 50,
  descriptionMaxLength: 200,
  maxDepreciationYears: 100
};

export interface PropertyAmenityDetails {
  id: number;
  propertyId: number;
  typeId: number;
  /** mm/dd/yyyy */
  serviceStartDate: string;
  depreciationYears: number;
  cost: number;
  brand: string;
  model: string;
  description: string;
  typeName?: string;
}

export interface PropertyAmenityFormRow {
  id: number;
  typeId: number;
  /** mm/dd/yyyy */
  serviceStartDate: string;
  depreciationYears: number;
  cost: number;
  brand: string;
  model: string;
  description: string;
  typeName?: string;
}

export interface PropertyAmenityInput {
  id?: number;
  typeId: number;
  /** mm/dd/yyyy */
  serviceStartDate: string;
  depreciationYears: number;
  cost: number;
  brand?: string;
  model?: string;
  description?: string;
}

export interface ContactDetails {
  id: number;
  firstName: string;
  lastName: string;
  businessName?: string;
}

export interface PropertyAddressInput {
  id?: number;
  addressId: number;
  categoryId: number;
  isPrimary?: boolean;
}

export interface PropertyFeeInput {
  id?: number;
  typeId: number;
  description: string;
  amount: number;
  contactId: number;
  interval: PropertyFeeInterval;
  /** mm/dd/yyyy */
  feeDate: string;
}

export interface CreatePropertyRequest {
  name: string;
  description?: string;
  propertiesAddresses: PropertyAddressInput[];
  propertiesFees?: PropertyFeeInput[];
  propertiesAmenities?: PropertyAmenityInput[];
  contactIds?: number[];
}

export interface UpdatePropertyRequest {
  id: number;
  name?: string;
  description?: string;
  propertiesAddresses?: PropertyAddressInput[];
  propertiesFees?: PropertyFeeInput[];
  propertiesAmenities?: PropertyAmenityInput[];
  contactIds?: number[];
}

export interface PropertyFormData {
  name: string;
  description: string;
  propertiesAddresses: PropertyAddressFormRow[];
  propertiesFees: PropertyFeeFormRow[];
  propertiesAmenities: PropertyAmenityFormRow[];
  contactIds: number[];
}

export interface PropertySearchParams {
  search?: string;
  fields?: string[];
  page?: number;
  limit?: number;
  sorts?: Array<{
    field: 'name' | 'description' | 'dateCreated' | 'dateUpdated';
    direction: 'asc' | 'desc';
  }>;
}
